import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import {DevPlatformVersion} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import * as Colors from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import WarningIcon from 'ui-library/icons/WarningIcon';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

import {ListEventInfo} from './__generated/ListEventInfo';
import {trackDocumentationClick} from './EventSubscriptionsPage';

type EventInfoNode = ListEventInfo['eventInfoListing']['eventInfos'][number];

export const EVENT_INFO_LISTING_QUERY = gql`
  query ListEventInfo @skipAudit @engComponent(name: EVENTS) {
    eventInfoListing {
      eventInfos {
        description
        version
        versionedEventType
        deprecationDate
      }
    }
  }
`;

export function makeOptionDatas(
  eventInfos: Array<EventInfoNode>,
  eventTypes: Array<string>
): Array<OptionData> {
  const deprecatedeventInfos = eventInfos.filter((eventInfo) => eventInfo.deprecationDate != null);
  const nondeprecatedeventInfos = eventInfos.filter((eventInfo) => eventInfo.deprecationDate == null);
  // Create mapping from event.version to eventInfo sorted alphabetically by versionedEventType
  const nonDeprecatedVersionToeventInfo = _.groupBy(
    _.sortBy(nondeprecatedeventInfos, 'versionedEventType'),
    'version'
  );

  // eslint-disable-next-line no-restricted-syntax
  const sortedVersions = Object.keys(nonDeprecatedVersionToeventInfo).sort();

  const output = [];
  for (const version of sortedVersions) {
    output.push({name: version, isHeader: true});
    for (const eventInfo of nonDeprecatedVersionToeventInfo[version]) {
      output.push({
        name: eventInfo.versionedEventType,
        value: eventInfo.versionedEventType,
        disabled: eventTypes.includes(eventInfo.versionedEventType),
      });
    }
  }
  if (deprecatedeventInfos.length > 0) {
    output.push({name: 'Deprecated', isHeader: true});
    for (const eventInfo of deprecatedeventInfos) {
      output.push({
        name: eventInfo.versionedEventType,
        value: eventInfo.versionedEventType,
        disabled: eventTypes.includes(eventInfo.versionedEventType),
      });
    }
  }
  return output;
}

export function getDescriptionFromVersionedEventType(
  eventInfos: Array<EventInfoNode>,
  versionedEventType: string
): string {
  const matchingEventInfo = eventInfos.find(
    (eventInfo) => eventInfo.versionedEventType === versionedEventType
  );
  assert(matchingEventInfo != null, `Expected matching eventInfo for type ${versionedEventType}`);
  return matchingEventInfo.description;
}

interface Props {
  eventTypes: Array<string>;
  onChangeEventTypes: (eventTypes: Array<string>) => void;
}

export default function EventTypesSection(props: Props): JSX.Element {
  function handleAddEventType(eventType: string): void {
    props.onChangeEventTypes([...props.eventTypes, eventType]);
  }

  function handleRemoveEventType(eventType: string): void {
    props.onChangeEventTypes(props.eventTypes.filter((type) => type !== eventType));
  }

  function isAlphaBetaSelected(eventInfos: Array<EventInfoNode>): boolean {
    return (
      eventInfos.find(
        (node) =>
          (node.version === DevPlatformVersion.V2_BETA &&
            props.eventTypes.includes(node.versionedEventType)) ||
          (node.version === DevPlatformVersion.V2_ALPHA && props.eventTypes.includes(node.versionedEventType))
      ) !== undefined
    );
  }

  function isDeprecatedSelected(eventInfos: Array<EventInfoNode>): boolean {
    return (
      eventInfos.find(
        (node) => node.deprecationDate != null && props.eventTypes.includes(node.versionedEventType)
      ) !== undefined
    );
  }

  return (
    <BenchlingQuery<ListEventInfo>
      query={EVENT_INFO_LISTING_QUERY}
      renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
    >
      {(data) => (
        <div className={classNames('container', css(styles.container))}>
          <SelectDropdown
            className="u-small-spacing-top u-small-spacing-bottom"
            menu={
              <SearchableMenu
                width={Menu.WIDTHS.FULL}
                optionDatas={makeOptionDatas(data.eventInfoListing.eventInfos, props.eventTypes)}
                onSelect={handleAddEventType}
              />
            }
            displayName="Add event type..."
            dataTestAttrs={{component: 'SelectDropdown', element: 'eventType'}}
          />
          <Row className="u-faded">
            <Col xs={4}>Event Type</Col>
            <Col xs={8}>Description</Col>
          </Row>
          <hr className={css(styles.hr)} />
          {props.eventTypes.length ? (
            props.eventTypes.map((eventType) => (
              <Row key={eventType} className="u-small-padding-bottom">
                <Col xs={4}>
                  <div className={classNames('u-no-padding', 'u-overflow-wrap-break-word')}>{eventType}</div>
                </Col>
                <Col id="__description-column" xs={7}>
                  {getDescriptionFromVersionedEventType(data.eventInfoListing.eventInfos, eventType)}
                </Col>
                <Col xs={1}>
                  <BenchlingIcon
                    icon="close"
                    className={classNames(css(styles.removeIcon), 'u-faded u-pointer')}
                    onClick={() => {
                      handleRemoveEventType(eventType);
                    }}
                  />
                </Col>
              </Row>
            ))
          ) : (
            <Row className="u-small-padding-bottom u-faded">
              <Col xs={12}>No event types selected</Col>
            </Row>
          )}
          {isAlphaBetaSelected(data.eventInfoListing.eventInfos) && (
            <div id="__alpha-beta-warning">
              <WarningIcon className="u-mini-spacing-right" />
              Selected at least one{' '}
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://docs.benchling.com/docs/events-reference#v2-alpha-events-reference"
                onClick={() => {
                  trackDocumentationClick('alpha');
                }}
              >
                alpha{' '}
              </a>
              or{' '}
              <a
                id="__beta_link"
                target="_blank"
                rel="noopener noreferrer"
                href="https://docs.benchling.com/docs/events-reference#v2-beta-events-reference"
                onClick={() => {
                  trackDocumentationClick('beta');
                }}
              >
                beta{' '}
              </a>
              event.
            </div>
          )}
          {isDeprecatedSelected(data.eventInfoListing.eventInfos) && (
            <div id="__deprecated-warning">
              <WarningIcon className="u-mini-spacing-right" />
              Selected at least one deprecated event.
            </div>
          )}
        </div>
      )}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.GRAY_LIGHTER,
  },
  hr: {
    marginTop: 9,
    marginBottom: 6,
    borderWidth: 1,
    borderColor: Colors.GRAY,
  },
  removeIcon: {
    height: '10px',
    width: '10px',
    marginTop: 3,
  },
});
